// The checklist templates: the lists a site visit usually follows, so a
// technician starts from the steps the trade expects instead of a blank
// page. Creating a checklist from a template copies its steps onto the
// property (shared/schemas/checklist.ts, worker/db/checklists.ts); the
// template itself is never edited, and a list made from one is then edited
// like any other. The key is stored on the list so a report can say which
// procedure it followed; a custom list stores '' (see storedTemplate).

export const CHECKLIST_TEMPLATE_KEYS = [
  'commissioning',
  'handover',
  'annual_service',
  'custom',
] as const;
export type ChecklistTemplateKey = (typeof CHECKLIST_TEMPLATE_KEYS)[number];

/** The template with no steps: the list is written on the property. */
export const CUSTOM_TEMPLATE_KEY: ChecklistTemplateKey = 'custom';

export interface ChecklistTemplate {
  readonly key: ChecklistTemplateKey;
  readonly name: string;
  /** The steps in order; empty for a custom list. */
  readonly items: readonly string[];
}

export const CHECKLIST_TEMPLATES: Readonly<Record<ChecklistTemplateKey, ChecklistTemplate>> = {
  commissioning: {
    key: 'commissioning',
    name: 'Commissioning',
    items: [
      'Label every run at both ends',
      'Test every run and record the result',
      'Photograph the rack front and rear',
      'Record make, model, and serial for every device',
      'Record admin credentials for every managed device',
      'Give every device a static address or a DHCP reservation and record it',
      'Record every WAN link with its ISP and account',
      'Back up the gateway configuration and attach it',
      "Confirm every access point's SSIDs and channels",
      'Hand over Wi-Fi and portal details to the client',
    ],
  },
  handover: {
    key: 'handover',
    name: 'Handover',
    items: [
      'Walk the client through the rack',
      'Confirm contact details and site access',
      'Confirm the report was sent',
      'Confirm warranty registrations',
    ],
  },
  annual_service: {
    key: 'annual_service',
    name: 'Annual service',
    items: [
      'Check UPS runtime and battery date',
      'Check firmware on gateway, switches, and access points',
      'Check camera views and NVR retention',
      'Re-test spare runs',
      'Refresh photos of the rack',
      'Review credentials',
    ],
  },
  custom: { key: 'custom', name: 'Custom', items: [] },
};

/** What the `template` column holds for a key: the key, or '' for a custom list. */
export function storedTemplate(key: ChecklistTemplateKey): string {
  return key === CUSTOM_TEMPLATE_KEY ? '' : key;
}

/** Whether a string (a select's value, a stored column) names a template. */
export function isChecklistTemplateKey(value: string): value is ChecklistTemplateKey {
  return (CHECKLIST_TEMPLATE_KEYS as readonly string[]).includes(value);
}

/**
 * The template a stored value names, as a person reads it: "Commissioning",
 * "Custom" for '', or the raw key for one the registry no longer lists (the
 * row is older than the code; the key is still the truest thing to show).
 */
export function templateLabel(stored: string): string {
  const key = stored === '' ? CUSTOM_TEMPLATE_KEY : stored;
  return isChecklistTemplateKey(key) ? CHECKLIST_TEMPLATES[key].name : stored;
}
